import type { TypeUploadContext } from './createUploadContext';

// ----------------------------------------------------------------------------

export type TypeUploadState = 'uploading' | 'paused' | 'aborted';

export type TypeUploadController = {
    abort: () => void;
    pause: () => void;
    resume: () => void;
    getState: () => TypeUploadState;
};

// ----------------------------------------------------------------------------

/*
 * The caller's handle on a running upload. Abort prefers the registry's `abortAll`, which
 * also records each XHR as aborted so the callbacks can tell cancellation from failure;
 * the inline loop is only a fallback for a context whose orchestrator never registered one.
 */
function createUploadController(context: TypeUploadContext): TypeUploadController {
    return {
        abort: function (): void {
            if (typeof context.abortAll === 'function') {
                context.abortAll();
                return;
            }

            if (context.abortedFlag.aborted) {
                return;
            }

            context.abortedFlag.aborted = true;

            // Same release as the registry's abortAll: a paused worker must not stay parked.
            context.pausedFlag.resume();

            for (const xhr of [...context.xhrList]) {
                try {
                    xhr.abort();
                } catch {
                    // ignore
                }
            }
        },
        pause: function (): void {
            context.pausedFlag.pause();
        },
        resume: function (): void {
            context.pausedFlag.resume();
        },
        getState: function (): TypeUploadState {
            if (context.abortedFlag.aborted) {
                return 'aborted';
            }
            if (context.pausedFlag.paused) {
                return 'paused';
            }
            return 'uploading';
        },
    };
}

// ----------------------------------------------------------------------------

export default createUploadController;
